import type { AdminMeResponse } from '@odonix/contracts';

/**
 * Сессия платформенной админки.
 *
 * Устроена проще, чем в `apps/webapp`, ровно настолько, насколько сама
 * задача проще: сотрудник Odonix не выбирает клинику, не работает без сети и
 * не открывает локальную базу — access-токен живёт в памяти, refresh в
 * HttpOnly-cookie, и это вся модель (docs/03-modules/20-platform-admin.md §6:
 * офлайн-режим здесь не проектировался, потому что не нужен).
 */

export type SessionState =
  | { status: 'restoring' }
  | { status: 'anonymous'; error: string | null; busy: boolean }
  | { status: 'mfa_required'; email: string; error: string | null; busy: boolean }
  | { status: 'ready'; staff: AdminMeResponse };

export interface SessionStore {
  snapshot: () => SessionState;
  subscribe: (listener: () => void) => () => void;
  restore: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  logout: () => Promise<void>;
  /** Единственная дверь наружу для остальных модулей клиента: добавляет
   *  заголовок и один раз перезапрашивает токен при истёкшем сроке. */
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>;
  /** «Забыли пароль?» — без сессии. Бросает только при отказе связи или формы. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Новый пароль по ссылке из письма — без сессии. Бросает с текстом сервера. */
  confirmPasswordReset: (token: string, password: string) => Promise<void>;
}

interface ApiError {
  code: string;
  message: string;
  fields?: Record<string, string>;
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiError;
    return body.message;
  } catch {
    return 'Не удалось выполнить запрос';
  }
}

export function createSessionStore(options: { baseUrl: string }): SessionStore {
  const base = options.baseUrl.replace(/\/+$/, '');
  let state: SessionState = { status: 'restoring' };
  let accessToken: string | null = null;
  let challengeToken: string | null = null;
  const listeners = new Set<() => void>();

  function setState(next: SessionState): void {
    state = next;
    for (const listener of listeners) listener();
  }

  async function refresh(): Promise<boolean> {
    const response = await fetch(`${base}/v1/auth/refresh`, { method: 'POST', credentials: 'include' });
    if (!response.ok) return false;
    const body = (await response.json()) as { accessToken: string };
    accessToken = body.accessToken;
    return true;
  }

  async function loadMe(): Promise<AdminMeResponse> {
    const response = await fetch(`${base}/v1/auth/me`, {
      headers: { authorization: `Bearer ${accessToken ?? ''}` },
    });
    if (!response.ok) throw new Error(await parseError(response));
    return (await response.json()) as AdminMeResponse;
  }

  return {
    snapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async restore() {
      try {
        const ok = await refresh();
        if (!ok) {
          setState({ status: 'anonymous', error: null, busy: false });
          return;
        }
        const staff = await loadMe();
        setState({ status: 'ready', staff });
      } catch {
        setState({ status: 'anonymous', error: null, busy: false });
      }
    },

    async login(email, password) {
      setState({ status: 'anonymous', error: null, busy: true });

      let response: Response;
      try {
        response = await fetch(`${base}/v1/auth/login`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, password }),
        });
      } catch {
        setState({ status: 'anonymous', error: 'Нет связи с сервером', busy: false });
        return;
      }

      if (!response.ok) {
        setState({ status: 'anonymous', error: await parseError(response), busy: false });
        return;
      }

      const body = (await response.json()) as
        | { status: 'mfa_required'; challengeToken: string }
        | { status: 'authenticated'; accessToken: string; staff: AdminMeResponse };

      if (body.status === 'mfa_required') {
        challengeToken = body.challengeToken;
        setState({ status: 'mfa_required', email, error: null, busy: false });
        return;
      }

      accessToken = body.accessToken;
      setState({ status: 'ready', staff: body.staff });
    },

    async verifyMfa(code) {
      if (state.status !== 'mfa_required' || challengeToken === null) return;
      setState({ ...state, busy: true, error: null });

      let response: Response;
      try {
        response = await fetch(`${base}/v1/auth/mfa/verify`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ challengeToken, code }),
        });
      } catch {
        setState({ ...state, busy: false, error: 'Нет связи с сервером' });
        return;
      }

      if (!response.ok) {
        const message = await parseError(response);
        // Статически TypeScript уже сузил `state` до `mfa_required` веткой
        // выше и потому считает эту проверку излишней — но между ней и этой
        // строкой был `await`, и за это время пользователь мог успеть нажать
        // «Назад» (`cancelMfa`), сменив состояние. Без повторной проверки
        // здесь мы вернули бы его на экран кода, с которого он уже ушёл.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (state.status === 'mfa_required') {
          setState({ ...state, busy: false, error: message });
        }
        return;
      }

      const body = (await response.json()) as { accessToken: string; staff: AdminMeResponse };
      challengeToken = null;
      accessToken = body.accessToken;
      setState({ status: 'ready', staff: body.staff });
    },

    cancelMfa() {
      challengeToken = null;
      setState({ status: 'anonymous', error: null, busy: false });
    },

    async logout() {
      try {
        await fetch(`${base}/v1/auth/logout`, { method: 'POST', credentials: 'include' });
      } finally {
        accessToken = null;
        setState({ status: 'anonymous', error: null, busy: false });
      }
    },

    async authorizedFetch(path, init) {
      const doFetch = (): Promise<Response> => {
        // `Headers`, не разворот объектом: `init.headers` бывает `Headers`
        // или массивом пар `[string, string][]`, и `{ ...init.headers }`
        // в обоих случаях молча теряет заголовки вызывающего кода.
        const headers = new Headers(init?.headers);
        headers.set('authorization', `Bearer ${accessToken ?? ''}`);
        return fetch(`${base}${path}`, { ...init, credentials: 'include', headers });
      };

      let response = await doFetch();

      if (response.status === 401) {
        const ok = await refresh().catch(() => false);
        if (!ok) {
          setState({ status: 'anonymous', error: null, busy: false });
          return response;
        }
        response = await doFetch();
      }

      return response;
    },

    async requestPasswordReset(email) {
      const response = await fetch(`${base}/v1/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(await parseError(response));
    },

    async confirmPasswordReset(token, password) {
      const response = await fetch(`${base}/v1/auth/password-reset/confirm`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!response.ok) throw new Error(await parseError(response));
    },
  };
}
